import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Product, SiteApiService } from '../../core/services/api.service';

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html'
})
export class ProductsComponent implements OnInit, AfterViewInit, OnDestroy {

    products: Product[] = [];
    filteredProducts: Product[] = [];

    categories: string[] = ['All Products'];

    selectedCategory = 'All Products';
    searchText = '';

    loading = false;
    errorMessage = '';

    private observer: IntersectionObserver | null = null;

    constructor(
        public siteApi: SiteApiService,
        private elementRef: ElementRef,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            if ((params.get('category') || '').toLowerCase() === 'all') {
                this.showAllProducts(true);
            }
        });

        this.getProducts();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.initRevealAnimation();
        }, 300);
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    getProducts(): void {
        this.loading = true;
        this.errorMessage = '';

        this.siteApi.getProducts().subscribe(
            (data: Product[]) => {
                this.products = data || [];

                const uniqueCategories = Array.from(
                    new Set(
                        this.products
                            .map(x => x.groupName)
                            .filter(x => !!x)
                    )
                );

                this.categories = ['All Products'].concat(uniqueCategories);

                this.applyFilter();

                this.loading = false;

                setTimeout(() => {
                    this.initRevealAnimation();
                }, 150);
            },
            (error) => {
                console.error(error);
                this.errorMessage = 'Products could not be loaded.';
                this.loading = false;
            }
        );
    }


    private scrollToProductsTop(): void {
        const element = this.elementRef.nativeElement.querySelector('#productListTop') as HTMLElement;

        if (!element) {
            return;
        }

        const headerOffset = 105;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }

    private scrollToPageTop(): void {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    private showAllProducts(scrollTop: boolean = false): void {
        this.searchText = '';
        this.selectedCategory = 'All Products';
        this.applyFilter();

        if (scrollTop) {
            setTimeout(() => {
                this.scrollToPageTop();
                this.initRevealAnimation();
            }, 80);
        }
    }


    selectCategory(category: string, clearSearch: boolean = false): void {
        if (!category) {
            return;
        }

        if (clearSearch) {
            this.searchText = '';
        }

        this.selectedCategory = category;
        this.applyFilter();

        setTimeout(() => {
            this.scrollToProductsTop();
            this.initRevealAnimation();
        }, 80);
    }
    applyFilter(): void {
        const q = (this.searchText || '').toLowerCase().trim();

        this.filteredProducts = this.products.filter(p => {
            const categoryMatch =
                this.selectedCategory === 'All Products' ||
                p.groupName === this.selectedCategory;

            const searchMatch =
                !q ||
                (p.name || '').toLowerCase().indexOf(q) > -1 ||
                (p.groupName || '').toLowerCase().indexOf(q) > -1 ||
                String(p.id || '').indexOf(q) > -1;

            return categoryMatch && searchMatch;
        });

        setTimeout(() => {
            this.initRevealAnimation();
        }, 80);
    }

    onImageError(event: Event): void {
        const img = event.target as HTMLImageElement;
        img.src = 'assets/img/products/no-image.png';
    }

    private initRevealAnimation(): void {
        const elements = this.elementRef.nativeElement.querySelectorAll('.reveal');

        if (!elements || elements.length === 0) {
            return;
        }

        if (this.observer) {
            this.observer.disconnect();
        }

        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const el = entry.target as HTMLElement;
                        el.classList.add('show');
                        this.observer?.unobserve(el);
                    }
                });
            },
            {
                threshold: 0.14
            }
        );

        elements.forEach((el: HTMLElement) => {
            el.classList.remove('show');
            this.observer?.observe(el);
        });
    }
}
